import { getEquippedFor } from './equipped-cache.js';
import { loadLottiePlayer } from './lottie-loader.js';

// <retro-profile-effect username="...">
//
// Hero-tier-only overlay (per docs/store-asset-specs.md §3.4). Mounts
// the user's equipped profile-effect as a pointer-events: none layer
// inside the host card.
//   .json image_url → lazy-load dotlottie-web, mount via new DotLottie({ canvas, ... })
//   .svg / .png     → plain <img>
//   no effect       → empty (element takes zero render space because
//                     its only sizing comes from position:absolute inset:0)
//
// Note: @lottiefiles/dotlottie-web exports a programmatic DotLottie
// class — it does NOT register a <dotlottie-player> custom element
// (that's a different package, @lottiefiles/dotlottie-wc). We create
// a <canvas> and bind the DotLottie instance to it directly.

class RetroProfileEffect extends HTMLElement {
  static observedAttributes = ['username'];

  connectedCallback() { this._render(); }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal !== newVal && this.isConnected) this._render();
  }

  disconnectedCallback() {
    if (this._resizeObs) {
      try { this._resizeObs.disconnect(); } catch { /* ignore */ }
      this._resizeObs = null;
    }
    // Destroy any Lottie instance to release decoder resources.
    if (this._lottie) {
      try { this._lottie.destroy(); } catch { /* tolerate cleanup race */ }
      this._lottie = null;
    }
  }

  async _render() {
    // Tear down any prior Lottie instance + resize observer before re-rendering.
    if (this._resizeObs) {
      try { this._resizeObs.disconnect(); } catch { /* ignore */ }
      this._resizeObs = null;
    }
    if (this._lottie) {
      try { this._lottie.destroy(); } catch { /* tolerate cleanup race */ }
      this._lottie = null;
    }

    const username = this.getAttribute('username');
    if (!username) { this.innerHTML = ''; return; }
    const equipped = await getEquippedFor(username);
    if (!this.isConnected || this.getAttribute('username') !== username) return;

    const url = equipped['profile-effect']?.image_url;
    this.innerHTML = '';
    if (!url) return;

    if (url.toLowerCase().endsWith('.json')) {
      let mod;
      try {
        mod = await loadLottiePlayer();
      } catch {
        return; // silent fallback — no effect rendered
      }
      if (!this.isConnected) return;
      const canvas = document.createElement('canvas');
      canvas.setAttribute('aria-hidden', 'true');
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.display = 'block';
      this.appendChild(canvas);
      // Canvas backing store defaults to 300×150 regardless of CSS;
      // size it explicitly so DotLottie renders at full host size with
      // crisp DPR-aware pixels.
      const syncCanvasSize = () => {
        const dpr = window.devicePixelRatio || 1;
        const w = Math.max(1, Math.floor(this.clientWidth * dpr));
        const h = Math.max(1, Math.floor(this.clientHeight * dpr));
        if (canvas.width !== w) canvas.width = w;
        if (canvas.height !== h) canvas.height = h;
      };
      syncCanvasSize();
      this._resizeObs = new ResizeObserver(() => {
        syncCanvasSize();
        if (this._lottie && typeof this._lottie.resize === 'function') {
          try { this._lottie.resize(); } catch { /* ignore */ }
        }
      });
      this._resizeObs.observe(this);
      try {
        this._lottie = new mod.DotLottie({
          canvas,
          src: url,
          loop: true,
          autoplay: true,
          // Cover so particles fill the hero card instead of letter-
          // boxing into the Lottie composition's native aspect.
          layout: { fit: 'cover', align: [0.5, 0.5] },
        });
      } catch {
        // If DotLottie throws (e.g. corrupted asset), strip the canvas.
        this.innerHTML = '';
      }
    } else {
      const img = document.createElement('img');
      img.src = url;
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      this.appendChild(img);
    }
  }
}

if (!customElements.get('retro-profile-effect')) {
  customElements.define('retro-profile-effect', RetroProfileEffect);
}
